// T22.656 (Kelly 2026-10-22, MEASURED web-stg main.ac19251d.js @390): coming
// back to the Gallery from a wheel (Gallery -> View Wheel -> back) unmounted
// and remounted the whole route, and every piece of Gallery state that lived in
// component state started from zero again: the GalleryBootBoundary's `ready`
// flag ("Loading your Wheels" hero loader 198-577 ms), Gallery's own
// `initialLoadComplete` gate, the profile-wheel avatar frame (empty avatar),
// and the Created-wheels count (blank "Wheels" stat for ~4.4 s). The page
// re-ran its full cold-boot sequence on every return instead of showing what
// it had already painted a moment ago.
//
// This module-level store survives the route unmount (it lives for the life of
// the tab, like react-query's cache) and is used ONLY as the initial value of
// that state on a later mount. Every fetch still runs on mount and replaces the
// value when it lands (stale-while-revalidate), so nothing can stay stale past
// the refetch. Keys are the gallery's user id, so one profile's values never
// paint on another profile's gallery.

const bootedGalleries = new Set();
const profileWheels = new Map();
const createdWheelCounts = new Map();
const rootFolderLists = new Map();

const keyOf = (id) => (id == null || id === "" ? null : String(id));

export function markGalleryBooted(id) {
  const k = keyOf(id);
  if (k) bootedGalleries.add(k);
}

export function hasGalleryBooted(id) {
  const k = keyOf(id);
  return !!k && bootedGalleries.has(k);
}

export function rememberProfileWheel(id, wheel) {
  const k = keyOf(id);
  if (!k) return;
  if (wheel) profileWheels.set(k, wheel);
  else profileWheels.delete(k);
}

export function lastProfileWheel(id) {
  const k = keyOf(id);
  return (k && profileWheels.get(k)) || null;
}

export function rememberCreatedWheelCount(id, count) {
  const k = keyOf(id);
  if (!k) return;
  if (count == null) createdWheelCounts.delete(k);
  else createdWheelCounts.set(k, count);
}

// Returns the `{ id, count }` shape Profile/index.jsx keeps in state, or the
// "not loaded yet" `{ id: null, count: null }` when this profile has no count.
export function lastCreatedWheelCountState(id) {
  const k = keyOf(id);
  if (k && createdWheelCounts.has(k)) {
    return { id, count: createdWheelCounts.get(k) };
  }
  return { id: null, count: null };
}

// T22.656 audit-10221811-box-kelly-gallery-discover-4 (MEASURED web-stg
// main.21b7082f.js @390, QA Pro 55956): after the fix above the loader, avatar
// and count came back instantly, but the collections row still came back as 12
// grey skeleton circles for up to 4.3 s, because OwnerView's unmount cleanup
// empties the redux folder tree (resetFolders) and the remount waited for
// GET /api/v2/folders/ again (QA Pro 55956 has ZERO collections: the 12 are
// the 6 placeholder tiles, circle + label). Keep the owner's last ROOT
// collections list so the remount can paint it at once; the root fetch still
// runs and replaces it.
export function rememberRootFolders(id, list) {
  const k = keyOf(id);
  if (!k) return;
  // `null` = "never loaded" (forget); `[]` = "loaded, no collections" (kept,
  // so an owner with zero collections does not get skeletons back either).
  if (!Array.isArray(list)) rootFolderLists.delete(k);
  else rootFolderLists.set(k, list.filter((f) => f && !f._orphan));
}

// The cached root list (possibly empty), or null when there is none.
export function lastRootFolders(id) {
  const k = keyOf(id);
  return k && rootFolderLists.has(k) ? rootFolderLists.get(k) : null;
}

// Test-only: module state outlives a single test.
export function resetGalleryReturnCache() {
  bootedGalleries.clear();
  profileWheels.clear();
  createdWheelCounts.clear();
  rootFolderLists.clear();
}
